import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'
import type { User, EmploymentAgreement } from '@/types/user'
import type { PaginatedResponse } from '@/types/api'

/**
 * Hook for fetching user profile data.
 */

// Query keys for cache management
export const userKeys = {
  all: ['users'] as const,
  detail: (username: string) => [...userKeys.all, 'detail', username] as const,
  employments: (userId: string) => [...userKeys.all, 'employments', userId] as const,
  myEmployments: () => [...userKeys.all, 'employments', 'my'] as const,
}

/**
 * Hook to fetch a user by username.
 */
export function useUser(username: string | null) {
  return useQuery({
    queryKey: userKeys.detail(username || ''),
    queryFn: () => api.get<User>(`/api/users/username/${username}`),
    enabled: !!username,
    staleTime: 60000, // Cache for 1 minute
  })
}

/**
 * Hook to fetch the current user's employment agreements.
 * The API returns a paginated response; we extract the content array.
 */
export function useMyEmployments() {
  return useQuery({
    queryKey: userKeys.myEmployments(),
    queryFn: async () => {
      const response = await api.get<PaginatedResponse<EmploymentAgreement>>(
        '/api/v2/my/employmentagreements?page=0&size=100'
      )
      return response.content
    },
    staleTime: 30000,
  })
}

/**
 * Hook to fetch a user's employment agreements.
 * The API returns a paginated response; we extract the content array.
 */
export function useUserEmployments(userId: string | null) {
  return useQuery({
    queryKey: userKeys.employments(userId || ''),
    queryFn: async () => {
      const response = await api.get<PaginatedResponse<EmploymentAgreement>>(
        `/api/v2/employmentagreements?userId=${userId}&page=0&size=100`
      )
      return response.content
    },
    enabled: !!userId,
    staleTime: 30000,
  })
}
